import { useMutation, useQuery } from '@apollo/client';
import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  Consent,
  ConsentDescription,
  ConsentsSection,
  ContentSection,
  CustomAbsoluteCentered,
  DocumentLinkContainer,
  DocumentLinkIcon,
  DocumentLinkSection,
  RedExclamationIcon
} from './ConsentsTab.styled';
import { Paragraph, Title } from '../Shared.styled';
import Checkmark from 'assets/icons/checkmark-green.svg?react';
import { Loader } from 'components/Loader/Loader';
import { Button as DocumentLinkButton } from 'components/v2/Buttons/Button/Button';
import { Button } from 'components/v2/Buttons/Button/Button';
import { Checkbox } from 'components/v2/Inputs/Checkbox/Checkbox';
import { Text } from 'components/v2/Typography/Text';
import { usePatient } from 'hooks/usePatient/usePatient';
import { utils } from 'kb-shared';
import theme from 'kb-shared/theme';
import { BugTracker } from 'kb-shared/utilities/bugTracker';
import { INTAKE_AND_TODO } from 'screens/Dashboard/ToDoWidget/ToDoWidget.graphql';
import { IntakeTodoResult } from 'screens/Dashboard/ToDoWidget/ToDoWidget.types';
import { PATIENT_CONSENT_SUBMIT } from 'screens/InformedConsent/Components/ConsentAcceptAction/ConsentAcceptAction.graphql';
import { setReturnBackToBookingRoute } from 'screens/InformedConsent/ConsentStateManager.utils';
import { COMPLETE_FORM } from 'screens/InformedConsent/InformedConsent.graphql';
import { CompleteFormResponseData } from 'screens/InformedConsent/InformedConsent.types';
import { filterConsentsWithLinkUrl } from 'utilities/consent';
import { RoutesPath } from 'utilities/pageUrl';

import { ConsentsCompleted } from './ConsentsCompleted';
import { FinancialConsentInfo } from './FinancialConsentInfo';
import { STATUS } from '../../InterstitialModal.graphql';
import { Consent as ConsentData, StatusProp } from '../../InterstitialModal.types';

interface CreatePatientConsentResponseData {
  createPatientConsent: {
    id: string;
    patientConsent: {
      id: string;
    };
  };
}

export const ConsentsTab = ({ status }: StatusProp) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { hasRequiredConsents, requiredConsents } = status;
  const requiredConsentsWithLinkUrl = filterConsentsWithLinkUrl(requiredConsents);

  const { patient } = usePatient();

  const [checkedConsents, setCheckedConsents] = useState<{ [key: string]: boolean }>({});
  const [isButtonClicked, setIsButtonClicked] = useState<boolean>(false);

  const [completeForm, { loading }] = useMutation<
    CompleteFormResponseData,
    { patientConsentId: number; versionUuid: string | null }
  >(COMPLETE_FORM, {
    refetchQueries: [{ query: STATUS }],
    onError: error => BugTracker.notify(error, 'InterstitialCompleteConsentFormResponseData')
  });

  useQuery<IntakeTodoResult>(INTAKE_AND_TODO, {
    variables: { formId: utils.intakeFormId(patient?.gender) },
    skip: !hasRequiredConsents || !patient?.gender,
    onError: error => BugTracker.notify(error, 'ToDosFetch')
  });

  const [createPatientConsent, { loading: loadingCreateConsent }] = useMutation<
    CreatePatientConsentResponseData,
    { consentId: number; versionUuid: string | null }
  >(PATIENT_CONSENT_SUBMIT, {
    onError: error => BugTracker.notify(error, 'InterstitialCreatePatientConsentResponseData')
  });

  const finishCheckedConsents = async (consentsToFinish: { [key: string]: boolean }) => {
    const consentsCheckedIdList = Object.keys(consentsToFinish).filter(id => consentsToFinish[id]);

    try {
      for (const consentToFinishId of consentsCheckedIdList) {
        const selectedConsent = requiredConsentsWithLinkUrl.find(
          c => c.id === consentToFinishId && !c.completed
        );

        if (!selectedConsent?.linkUrl) continue;

        const consentId = parseInt(selectedConsent.linkUrl.split('/').pop() || '');
        const versionUuid = selectedConsent.versionUuid;

        await createPatientConsent({
          variables: { consentId, versionUuid: versionUuid }
        });

        await completeForm({
          variables: {
            patientConsentId: Number(consentToFinishId),
            versionUuid: versionUuid
          }
        });
      }
    } catch (error) {
      BugTracker.notify(error, 'InterstitialCompleteConsents');
    } finally {
      setIsButtonClicked(false);
    }
  };

  if (hasRequiredConsents === true) return <ConsentsCompleted />;

  const consents = [...requiredConsentsWithLinkUrl].sort((a, b) => b.id.localeCompare(a.id));
  const isFinancialConsent = (consent: ConsentData) => consent.name === 'Financial Consent';

  const updateConsentsChecked = (id: string) => {
    setCheckedConsents(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const selectedConsentsCount = requiredConsentsWithLinkUrl.filter(
    rc => checkedConsents[rc.id] && !rc.completed
  ).length;

  const buttonText = `I Have Reviewed And I Accept Selected Consents ${
    selectedConsentsCount ? `(${selectedConsentsCount})` : ''
  }`;

  return (
    <ConsentsSection>
      {(loading || loadingCreateConsent || isButtonClicked) && (
        <CustomAbsoluteCentered>
          <Loader />
        </CustomAbsoluteCentered>
      )}
      <ContentSection>
        <Text tag="span" size="lg" fontStyle="semibold">
          I am at least 18 years of age and I have read and accept:
        </Text>
      </ContentSection>
      <ContentSection>
        {consents.map(consent => (
          <Consent key={consent.id}>
            <Checkbox
              id={consent.id}
              disabled={consent.completed}
              isChecked={consent.completed || checkedConsents[consent?.id]}
              onChange={() => updateConsentsChecked(consent?.id)}
              isCheckedAndReviewed={consent.completed}
            />
            <ConsentDescription>
              <Title>{consent.name}</Title>
              <Paragraph>{consent.description}</Paragraph>
              {isFinancialConsent(consent) && <FinancialConsentInfo />}
            </ConsentDescription>
            {consent.completed ? (
              <Checkmark className="checkmark" />
            ) : (
              <DocumentLinkSection>
                <DocumentLinkContainer>
                  <DocumentLinkButton
                    category="secondary"
                    size={'sm'}
                    onClick={() => {
                      if (location.pathname.startsWith(RoutesPath.BOOKING_BOOK)) {
                        setReturnBackToBookingRoute(location.pathname, location.search);
                      }
                      navigate(consent.linkUrl);
                    }}
                    leftIcon={<DocumentLinkIcon />}
                  />
                </DocumentLinkContainer>
              </DocumentLinkSection>
            )}
          </Consent>
        ))}
      </ContentSection>
      {hasRequiredConsents ? (
        <ContentSection>
          <Checkmark className="checkmark" />
          <Text tag="span" size="sm" fontStyle="medium" color={theme.colors.green.darkestGreen}>
            All consents reviewed and accepted!
          </Text>
        </ContentSection>
      ) : (
        <ContentSection>
          <RedExclamationIcon />
          <Text tag="span" size="sm" fontStyle="medium" color={theme.colors.semantic.darkRed}>
            {' '}
            All consents must be reviewed and accepted!
          </Text>
        </ContentSection>
      )}
      <Button
        label={buttonText}
        category="primary-dark"
        isDisabled={selectedConsentsCount === 0 || isButtonClicked || hasRequiredConsents}
        onClick={() => {
          setIsButtonClicked(true);
          finishCheckedConsents(checkedConsents);
        }}
        fullWidth
      />
    </ConsentsSection>
  );
};
